import React from "react";
import { Box, Stack, Typography, useMediaQuery } from "@mui/material";
import LandingCanvas from "../landingEditor/LandingCanvas";
import { getLandingPreviewDevice } from "./previewDevices";

export default function LandingStudioCanvasStage({
  form,
  device,
  productsEnabled,
  activeSection,
  saving,
  inspectorOpen = false,
  onSelectSection,
  onCommit,
  onCommitMany,
  onUploadImage,
  onRemoveImage,
  onOpenSegmentTool,
  onToggleSection,
}) {
  const stageRef = React.useRef(null);
  const compactEditor = useMediaQuery("(max-width:1100px)");
  const previewDevice = getLandingPreviewDevice(device);
  const horizontalGutter = previewDevice.id === "mobile" ? 0 : 32;

  React.useEffect(() => {
    if (!activeSection) return undefined;
    const timer = window.setTimeout(() => {
      const stage = stageRef.current;
      const target = stage?.querySelector?.('[data-landing-section-active="true"]');
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    }, 40);
    return () => window.clearTimeout(timer);
  }, [activeSection, previewDevice.id, inspectorOpen]);

  return (
    <Box
      ref={stageRef}
      component="main"
      sx={{
        position: "fixed",
        inset: {
          xs: "136px 0 calc(76px + env(safe-area-inset-bottom))",
          sm: "88px 0 139px",
        },
        right: 0,
        "@media (min-width: 1101px)": {
          right: inspectorOpen ? "410px" : 0,
        },
        overflow: "auto",
        bgcolor: "#E9ECF0",
        scrollPaddingBlock: "72px",
        transition: compactEditor ? "none" : "right .18s ease",
      }}
    >
      <Box
        sx={{
          minWidth: `${previewDevice.width + horizontalGutter}px`,
          minHeight: "100%",
          px: previewDevice.id === "mobile" ? 0 : 2,
          py: 1.5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{
            width: `${previewDevice.width}px`,
            maxWidth: "none",
            minHeight: 28,
            mb: 1,
            px: 0.25,
            color: "rgba(25,36,47,.64)",
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 900 }}>
            {previewDevice.label} · {previewDevice.width}px
          </Typography>
          {saving ? (
            <Typography variant="caption" role="status" aria-live="polite" sx={{ fontWeight: 850 }}>
              Guardando borrador…
            </Typography>
          ) : null}
        </Stack>

        <Box
          data-landing-preview-device={previewDevice.id}
          sx={{
            width: `${previewDevice.width}px`,
            maxWidth: "none",
            minHeight: "calc(100% - 38px)",
            bgcolor: "#fff",
            borderRadius: previewDevice.id === "mobile" ? 2.5 : previewDevice.id === "tablet" ? 2 : 1.5,
            outline: "1px solid rgba(25,36,47,.12)",
            boxShadow: previewDevice.id === "desktop"
              ? "0 14px 42px rgba(25,36,47,.12)"
              : "0 18px 48px rgba(25,36,47,.18)",
            overflow: "hidden",
          }}
        >
          <LandingCanvas
            form={form}
            activeSection={activeSection}
            device={previewDevice.id}
            productsEnabled={productsEnabled}
            onSelectSection={onSelectSection}
            onCommit={onCommit}
            onCommitMany={onCommitMany}
            onUploadImage={onUploadImage}
            onRemoveImage={onRemoveImage}
            saving={saving}
            studioMode
            onOpenSegmentTool={onOpenSegmentTool}
            onToggleSection={onToggleSection}
          />
        </Box>
      </Box>
    </Box>
  );
}
